/**
 * The skeleton `showScrollSkeleton` paints: skeleton rows generated from the table's own
 * columns, drawn once as an SVG tile that repeats down the stretches of the windowed body
 * that hold no mounted row.
 *
 * A tile rather than elements, because the skeleton exists for the moment the page is too
 * busy to render: a background is painted ahead of the scroll by the browser alone, and an
 * element would wait for the same render the rows are waiting for.
 */
import type { RowData, Table } from '@tanstack/react-table'
import type { VirtualItem } from '@tanstack/react-virtual'

import { colors, radius } from '@gto/ui/theme/design-tokens'

import type { DataTableSkeletonCell } from '../data-table.types'
import { readColumnAlign, readColumnMeta } from './column-meta'
import type { DataTableAlign } from './data-table.styles'
import type { PinnedGeometryMemoKey } from './pinning.styles'

/**
 * How long each row of the tile draws a text bar, against the column's share. Three rows of
 * different lengths, so a stretch does not read as one row stamped down the body.
 */
export const SKELETON_TEXT_LENGTHS = [1, 0.72, 0.86] as const

/** A text bar's share of its cell's content width, where the column names none. */
const DEFAULT_TEXT_SHARE = 0.6

/** A text bar is this share of the row's height, within the bounds below. */
const TEXT_BAR_SHARE_OF_ROW = 0.4

/** The height of a line of 14px text, the size the body cells set. */
const TEXT_BAR_MAX_PX = 12

const TEXT_BAR_MIN_PX = 6

/** `px-3` on `dataTableCellVariants`: 12px of each column is padding on either side. */
const CELL_PADDING_X_PX = 12

const ROUNDED_END_PX = parseFloat(radius.xsm)

/** The placeholder row's fill (`bg-action-hover`), so the two read as one loading surface. */
const BAR_FILL = colors.action.hover

/** One column as the skeleton sees it, in the order the row lays the columns out. */
export type ScrollSkeletonTrack = {
  widthPx: number
  align: DataTableAlign
  cell: DataTableSkeletonCell | false
}

export type ScrollSkeletonTile = {
  /** The CSS `background-image` value. */
  image: string
  widthPx: number
  heightPx: number
}

/** A stretch of the windowed body that holds no mounted row, in px from the body's top. */
export type ScrollSkeletonStretch = {
  key: 'above' | 'below'
  fromPx: number
  toPx: number
}

/**
 * The columns a body row lays out, in its order: the selection gutter, blank, then the
 * left-pinned, centre and right-pinned columns, as `row.getVisibleCells()` places them.
 *
 * `_memoKey` is unused by the body on purpose, as in `getPinnedColumnOffsets`: this reads the
 * referentially-stable `table`, and the key is what lets a `useMemo` see the columns change.
 */
export const getScrollSkeletonTracks = <TData extends RowData>(
  table: Table<TData>,
  selectionGutterPx: number,
  _memoKey?: PinnedGeometryMemoKey<TData>,
): ScrollSkeletonTrack[] => {
  const gutter: ScrollSkeletonTrack[] =
    selectionGutterPx > 0 ? [{ widthPx: selectionGutterPx, align: 'center', cell: false }] : []
  const columns = [
    ...table.getLeftVisibleLeafColumns(),
    ...table.getCenterVisibleLeafColumns(),
    ...table.getRightVisibleLeafColumns(),
  ].map(
    (column): ScrollSkeletonTrack => ({
      widthPx: column.getSize(),
      align: readColumnAlign(column) ?? 'left',
      cell: readColumnMeta(column)?.skeleton ?? {},
    }),
  )

  return [...gutter, ...columns]
}

/**
 * The stretches of the windowed body above and below the mounted rows. Item offsets carry
 * the virtualizer's `scrollMargin` and the body does not, so it is taken off, as the rows'
 * own `translateY` does. With no row mounted, the whole body is one stretch.
 */
export const toUnmountedStretches = (
  items: readonly VirtualItem[],
  scrollMarginPx: number,
  totalSizePx: number,
): ScrollSkeletonStretch[] => {
  const first = items.at(0)
  const last = items.at(-1)
  const stretches: ScrollSkeletonStretch[] =
    first === undefined || last === undefined
      ? [{ key: 'above', fromPx: 0, toPx: totalSizePx }]
      : [
          { key: 'above', fromPx: 0, toPx: first.start - scrollMarginPx },
          { key: 'below', fromPx: last.end - scrollMarginPx, toPx: totalSizePx },
        ]

  return stretches.filter((stretch) => stretch.toPx > stretch.fromPx)
}

/** A column's paper surface, placed once; each row of the tile fills its whole slot with it. */
type CellSurface = { leftPx: number; widthPx: number; fill: string }

const toCellSurfaces = (placed: readonly PlacedTrack[]): CellSurface[] =>
  placed.flatMap(({ track, leftPx }) => {
    const surface = track.cell === false ? undefined : track.cell.surface
    if (surface === undefined) {
      return []
    }

    const insetStartPx = surface.insetStartPx ?? 0
    const widthPx = track.widthPx - insetStartPx - (surface.insetEndPx ?? 0)

    return widthPx > 0
      ? [{ leftPx: leftPx + insetStartPx, widthPx, fill: colors.paper[surface.elevation] }]
      : []
  })

const toSurfaceRect = (surface: CellSurface, rowTopPx: number, slotPx: number): string =>
  `<rect x="${surface.leftPx}" y="${rowTopPx}" width="${surface.widthPx}" height="${slotPx}" fill="${surface.fill}"/>`

/** One cell's bar, placed in its column once; each row of the tile then gives it a length. */
type CellBar = {
  align: DataTableAlign
  contentLeftPx: number
  contentWidthPx: number
  /** The bar's share of the content width, `0`–`1`. */
  share: number
  /** From the top of the row, so the bar sits centred in it. */
  topPx: number
  heightPx: number
  cornerPx: number
}

/** A track paired with where it starts along the row. */
type PlacedTrack = { track: ScrollSkeletonTrack; leftPx: number }

type TrackLefts = { placed: PlacedTrack[]; widthPx: number }

/** Where each track starts along the row, and the row's full width, in one pass. */
const toTrackLefts = (tracks: readonly ScrollSkeletonTrack[]): TrackLefts =>
  tracks.reduce<TrackLefts>(
    ({ placed, widthPx }, track) => ({
      placed: [...placed, { track, leftPx: widthPx }],
      widthPx: widthPx + track.widthPx,
    }),
    { placed: [], widthPx: 0 },
  )

const toTextBarHeight = (rowHeightPx: number): number =>
  Math.min(
    TEXT_BAR_MAX_PX,
    Math.max(TEXT_BAR_MIN_PX, Math.round(rowHeightPx * TEXT_BAR_SHARE_OF_ROW)),
  )

const toCellBars = (placed: readonly PlacedTrack[], rowHeightPx: number): CellBar[] =>
  placed.flatMap(({ track, leftPx }) => {
    if (track.cell === false) {
      return []
    }

    const heightPx = Math.min(track.cell.height ?? toTextBarHeight(rowHeightPx), rowHeightPx)

    return [
      {
        align: track.align,
        contentLeftPx: leftPx + CELL_PADDING_X_PX,
        contentWidthPx: track.widthPx - 2 * CELL_PADDING_X_PX,
        share: Math.min(Math.max(track.cell.width ?? DEFAULT_TEXT_SHARE, 0), 1),
        topPx: Math.round((rowHeightPx - heightPx) / 2),
        heightPx,
        cornerPx: track.cell.square === true ? 0 : Math.min(ROUNDED_END_PX, heightPx / 2),
      },
    ]
  })

const toBarX = (bar: CellBar, widthPx: number): number => {
  if (bar.align === 'right') {
    return bar.contentLeftPx + bar.contentWidthPx - widthPx
  }

  return bar.align === 'center'
    ? bar.contentLeftPx + Math.round((bar.contentWidthPx - widthPx) / 2)
    : bar.contentLeftPx
}

/** The bar on one row of the tile, or none where its cell is too narrow to hold one. */
const toRect = (bar: CellBar, length: number, rowTopPx: number): string[] => {
  // A bar that fills its cell is a track, and a track has one length on every row.
  const widthPx = Math.round(bar.contentWidthPx * bar.share * (bar.share === 1 ? 1 : length))
  if (widthPx <= 0) {
    return []
  }

  return [
    `<rect x="${toBarX(bar, widthPx)}" y="${rowTopPx + bar.topPx}" width="${widthPx}" height="${bar.heightPx}" rx="${bar.cornerPx}" fill="${BAR_FILL}"/>`,
  ]
}

type BuildScrollSkeletonTileInput = {
  tracks: readonly ScrollSkeletonTrack[]
  rowHeightPx: number
  /** One windowed slot: the row height plus the gap under it. */
  slotPx: number
}

export const buildScrollSkeletonTile = ({
  tracks,
  rowHeightPx,
  slotPx,
}: BuildScrollSkeletonTileInput): ScrollSkeletonTile => {
  const { placed, widthPx } = toTrackLefts(tracks)
  const cellBars = toCellBars(placed, rowHeightPx)
  const cellSurfaces = toCellSurfaces(placed)
  const heightPx = slotPx * SKELETON_TEXT_LENGTHS.length
  // The surfaces first, so each bar paints over its own.
  const rects = SKELETON_TEXT_LENGTHS.flatMap((length, rowIndex) => [
    ...cellSurfaces.map((surface) => toSurfaceRect(surface, rowIndex * slotPx, slotPx)),
    ...cellBars.flatMap((bar) => toRect(bar, length, rowIndex * slotPx)),
  ])
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${widthPx}" height="${heightPx}">${rects.join('')}</svg>`

  // Base64 rather than percent-encoded: both paint, but jsdom's CSS parser drops a
  // percent-encoded url, and the table's tests read this tile back off the element.
  return { image: `url("data:image/svg+xml;base64,${btoa(svg)}")`, widthPx, heightPx }
}
